import type { AxiosInstance } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import type { ExternalApiSetting } from './types'
import type { Message } from '@/types/resources/message'

// fetchCount answers with a bare number or `{ count }`. Callers pick their own default for null.
export function countFromResponse(value: unknown): number | null {
  if (typeof value === 'number') return value
  const count = (value as { count?: unknown } | null | undefined)?.count
  return typeof count === 'number' ? count : null
}

export type AccountStatsTimeRange = '7d' | '30d' | 'all'

export interface AccountMessagingStats {
  totalMessagesSent: number
  email: { totalFinished: number, openRate: number | null, clickRate: number | null }
  sms: { totalFinished: number, clickRate: number | null }
}

export default (axios: AxiosInstance) => ({
  async get(promoterOid: number, messageOid: number): Promise<Message> {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/task/${messageOid}`, {})
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Event error',
        message: `Error getting Event from event with oid of ${messageOid}`,
        status,
      }
      throw apiError
    }
    return data
  },

  /** Account-wide email and SMS figures. Rates are percentages with one decimal, null when nothing was sent. */
  async fetchAccountStats(promoterOid: number, timeRange: AccountStatsTimeRange = 'all'): Promise<AccountMessagingStats> {
    const { data, status } = await axios.post(`/promoter/${promoterOid}/marketing-stats`, { timeRange })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get account stats error',
        message: 'Error getting the account messaging stats',
        status,
      }
      throw apiError
    }
    return data
  },

  /** Realtime status counters of a message (more up to date than `statusDetails`). */
  async fetchStatsRealtime(
    promoterOid: number,
    messageOid: number,
  ): Promise<Message['statusDetails']> {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/task-stats-realtime/${messageOid}`)
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get message stats error',
        message: `Error getting stats of message ${messageOid}`,
        status,
      }
      throw apiError
    }
    return data
  },

  /** Events and links a message is linked to for conversion attribution. */
  async fetchAttributionSelectedData(
    promoterOid: number,
    messageOid: number,
  ): Promise<{ events: { oid: number, name?: string }[], links?: { oid: number, targetUrl: string }[] }> {
    const { data } = await axios.get(`/promoter/${promoterOid}/task/${messageOid}/attribution-selected-data`)
    return data
  },

  async fetchSmsMessagePreviewWithoutStats(
    promoterOid: number,
    message: string | null,
  ): Promise<{ message: string, charactersLeft: number, optOutMessage: string }> {
    const { data, status }: { data: SMSMessagePreview, status: number } = await axios.get(`/promoter/${promoterOid}/message-preview/`, {
      params: {
        $provider: 'sms',
        $body: message || '',
        $cost: false,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch SMS message preview',
        message: `Error fetching SMS message preview`,
        status,
      }
      throw apiError
    }

    return {
      message: data.body,
      charactersLeft: data.charactersLeft,
      optOutMessage: data.optOutMessage,
    }
  },
  /**
   * Fetch additional dynamic tags that are defined in "external-api-settings"
   * @param promoterOid
   * @returns
   */
  async fetchAdditionalDynamicTags(
    promoterOid: number,
  ): Promise<string[]> {
    const { data } = await axios.get(`/promoter/${promoterOid}/external-api-settings`)
    const externalApiSetting: ExternalApiSetting = data[0]
    if (externalApiSetting) {
      return externalApiSetting.defaultDynamicTags
    }
    else {
      return []
    }
  },
  async search(
    promoterOid: number,
    {
      top = 20,
      skip = 0,
      searchString = null,
      orderby = 'sys_ctime DESC',
      select = null,
      messageStatus = null,
      provider = null,
    }: { top: number; skip: number
      searchString: string | null
      select: string | null
      orderby?: string
      messageStatus: string | null
      provider: string | null },
  ): Promise<Message[]> {
    const uri = `/promoter/${promoterOid}/fan-message-search`
    const { data, status }: { data: Message[], status: number } = await axios.get(uri, {
      params: {
        $top: top,
        $skip: skip,
        $select: select || null,
        $search: searchString || null,
        $status: messageStatus || null,
        $provider: provider || null,
        // The server reads `$orderby`; a bare `orderby` is ignored (it falls back to sys_ctime DESC).
        $orderby: orderby || null,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Messages error',
        message: `Error fetching Messages`,
        status,
      }
      throw apiError
    }
    return data
  },
  async fetchCount(
    promoterOid: number,
    {
      searchString = null,
      messageStatus = null,
      provider = null,
    }: {
      searchString: string | null
      select: string | null
      orderby?: string
      messageStatus: string | null
      provider: string | null
    },
  ): Promise<Message[]> {
    const uri = `/promoter/${promoterOid}/fan-message-search`
    const { data, status }: { data: Message[], status: number } = await axios.get(uri, {
      params: {
        $top: 0,
        $count: true,
        $search: searchString || null,
        $status: messageStatus || null,
        $provider: provider || null,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Messages error',
        message: `Error fetching Messages`,
        status,
      }
      throw apiError
    }
    return data
  },
})
